import { ThemeEvents } from '@theme/events';

/**
 * @typedef {{ id: number, title: string, price: number, price_formatted?: string, compare_at_price: number, compare_at_price_formatted?: string, featured_image?: string }} SystemCardVariant
 */

class ProductSystemCard extends HTMLElement {
  constructor() {
    super();
    /** @type {EventListener} */
    this._onGlobalVariantUpdate = this.onGlobalVariantUpdate.bind(this);
    /** @type {boolean} */
    this._inited = false;
    /** @type {SystemCardVariant[]} */
    this.variants = [];
  }

  init() {
    this.inputs = this.querySelectorAll('input.product-system-card__swatch-input');
    this.variantNameElement = this.querySelector('[data-variant-name]');
    this.priceElement = this.querySelector('[data-price]');
    this.variantsJsonElement = this.querySelector('[data-variants-json]');

    if (this.variantsJsonElement) {
      try {
        this.variants = /** @type {SystemCardVariant[]} */ (
          JSON.parse(this.variantsJsonElement.textContent || '[]')
        );
      } catch (e) {
        console.error('Error parsing variants JSON', e);
        this.variants = [];
      }
    } else {
      this.variants = [];
    }

    for (const input of this.inputs) {
      input.addEventListener('change', this.onInternalVariantChange.bind(this));
    }
  }

  connectedCallback() {
    document.addEventListener(ThemeEvents.variantUpdate, this._onGlobalVariantUpdate);

    if (!this._inited) {
      this.init();
      this._inited = true;
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.variantUpdate, this._onGlobalVariantUpdate);
  }

  /** @param {CustomEvent} event */
  onGlobalVariantUpdate(event) {
    const variantId = event.detail.resource?.id;
    const productId = event.detail.data?.productId;

    if (this.dataset.productId && productId && this.dataset.productId !== String(productId)) return;

    if (variantId) {
      this.updateByVariantId(variantId);
    }
  }

  /** @param {Event} event */
  onInternalVariantChange(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!input.checked) return;
    this.updateByVariantId(input.value);
  }

  /**
   * Single hero image: resolve fresh after morphs (avoid stale ref).
   * @returns {HTMLImageElement | null}
   */
  getMainImageElement() {
    const el =
      this.querySelector('img.product-system-card__image') ||
      this.querySelector('.product-system-card__image-container img');
    return el instanceof HTMLImageElement ? el : null;
  }

  /**
   * One URL only: clear srcset/sizes (Liquid `artie-image` may emit them); wrong srcset slows swap.
   * @param {HTMLImageElement} img
   * @param {string} url
   */
  applyMainImageUrl(img, url) {
    if (!(img instanceof HTMLImageElement) || !url) return;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = url;
    const p = img.decode?.();
    if (p && typeof p.then === 'function') p.catch(() => {});
  }

  /** @param {string | number} variantId */
  updateByVariantId(variantId) {
    const idStr = String(variantId);
    const input = /** @type {HTMLInputElement | null} */ (
      this.querySelector(`input.product-system-card__swatch-input[value="${idStr}"]`)
    );
    const variant = this.variants.find((/** @type {SystemCardVariant} */ v) => String(v.id) === idStr);
    if (!input && !variant) return;

    const title =
      (input && input.dataset.title) || (variant && variant.title) || '';
    if (this.variantNameElement && title) {
      this.variantNameElement.textContent = title;
    }

    const imageUrl =
      (input && input.dataset.image) || (variant && variant.featured_image) || '';
    const img = this.getMainImageElement();
    if (img && imageUrl) {
      this.applyMainImageUrl(img, imageUrl);
    }

    if (this.priceElement) {
      const priceText =
        (input && input.dataset.price) ||
        (variant && (variant.price_formatted || this.formatMoney(variant.price))) ||
        '';
      if (priceText) this.priceElement.textContent = priceText;
    }

    const compareEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-compare-price]'));
    if (compareEl) {
      const compareText = (input && input.dataset.comparePrice) || '';
      const priceNum = variant ? variant.price : NaN;
      const compareNum = variant ? variant.compare_at_price : NaN;
      const showCompare =
        compareText ||
        (typeof compareNum === 'number' &&
          typeof priceNum === 'number' &&
          !Number.isNaN(compareNum) &&
          !Number.isNaN(priceNum) &&
          compareNum > priceNum);
      if (showCompare) {
        compareEl.textContent =
          compareText ||
          (variant && (variant.compare_at_price_formatted || this.formatMoney(variant.compare_at_price))) ||
          '';
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    const swatches = this.querySelectorAll('input.product-system-card__swatch-input');
    swatches.forEach((sw) => {
      const el = /** @type {HTMLInputElement} */ (sw);
      el.checked = String(el.value) === idStr;
    });
  }

  /** @param {number} cents */
  formatMoney(cents) {
    if (typeof cents !== 'number') return String(cents);
    return `$${(cents / 100).toFixed(2)}`;
  }
}

if (!customElements.get('product-system-card')) {
  customElements.define('product-system-card', ProductSystemCard);
}
